import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader, Upload, FileText, Search, AlertCircle } from 'lucide-react';
import { getPrescriptionService } from '../../domains/prescriptions';
import { getMungPayService } from '../../domains/mungpay';
import LogUtil from '../../core/utils/LogUtil';
import './prescriptionAnalysisUpload.css';

/**
 * 처방전 분석 업로드 페이지
 * 파일 선택 → S3 업로드 → 처방전 분석 API 호출 → 결과 페이지 이동
 */
const PrescriptionAnalysisUpload = () => {
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  // 파일 선택 핸들러
  const handleFileChange = event => {
    const file = event.target.files[0];
    if (!file) return;

    setSelectedFile(file);
    setError(null);

    // 미리보기 생성
    const reader = new FileReader();
    reader.onload = e => {
      setPreviewUrl(e.target.result);
    };
    reader.readAsDataURL(file);

    LogUtil.log('📎 [Prescription Upload] 파일 선택:', file.name);
  };

  // 분석 시작 핸들러
  const handleStartAnalysis = async () => {
    if (!selectedFile) {
      setError('파일을 업로드해주세요.');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      // S3에 업로드
      LogUtil.log('📤 [Prescription Upload] S3 업로드 시작...');
      const mungPayService = getMungPayService();
      const uploadResult = await mungPayService.uploadToS3(selectedFile, '60');

      if (!uploadResult.success) {
        throw new Error('파일 업로드에 실패했습니다.');
      }

      const targetUrl = uploadResult.imageUrl;
      LogUtil.log('✅ [Prescription Upload] S3 업로드 완료:', targetUrl);

      // 처방전 분석 API 호출
      await handleUploadAndAnalyze(targetUrl);
    } catch (error) {
      LogUtil.error('❌ [Prescription Upload] 처리 실패:', error);
      setError(error.message);
      setUploading(false);
    }
  };

  // 업로드 및 분석 실행
  const handleUploadAndAnalyze = async s3Url => {
    if (!s3Url) {
      setError('이미지 URL이 없습니다.');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      const prescriptionService = getPrescriptionService();

      // 처방전 분석 API 호출
      LogUtil.log('🔍 [Prescription Upload] 처방전 분석 시작...');
      const analysisResult = await prescriptionService.analyzePrescription({
        prescription_image_url: s3Url,
      });
      if (analysisResult?.resultCode !== 200) {
        throw new Error(
          `분석 실패: ${analysisResult?.resultMsg || '알 수 없는 오류'}`
        );
      }

      LogUtil.log('✅ [Prescription Upload] 분석 완료:', analysisResult);

      // 3. 결과 페이지로 이동 (state로 데이터 전달)
      navigate('/test/prescription/result', {
        state: {
          receiptUrl: s3Url,
          analysisResult: analysisResult.data,
        },
      });
    } catch (error) {
      LogUtil.error('❌ [Prescription Upload] 처리 실패:', error);
      setError(error.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="prescription-upload-container">
      {/* 헤더 */}
      <div className="prescription-upload-header">
        <h2 className="prescription-upload-title">처방전 분석 - 파일 업로드</h2>
      </div>

      {/* 컨텐츠 */}
      <div className="prescription-upload-content">
        {/* 업로드 섹션 */}
        <div className="prescription-upload-section">
          <h3 className="prescription-section-title">
            <Upload className="prescription-title-icon" /> 파일
            업로드
          </h3>
          <div className="prescription-upload-box">
            <input
              type="file"
              accept="image/jpeg,image/png,application/pdf"
              onChange={handleFileChange}
              className="prescription-file-input"
            />
            {!selectedFile ? (
              <div className="prescription-upload-placeholder">
                <FileText className="prescription-upload-icon" />
                <p className="prescription-upload-text">파일을 선택하세요</p>
                <p className="prescription-upload-subtext">
                  JPG, PNG, PDF 형식 지원
                </p>
              </div>
            ) : (
              <div className="prescription-preview-container">
                <img
                  src={previewUrl}
                  alt="미리보기"
                  className="prescription-preview-image"
                />
              </div>
            )}
          </div>
          {selectedFile && (
            <div className="prescription-file-info">
              <div className="prescription-file-name">{selectedFile.name}</div>
              <div className="prescription-file-size">
                {(selectedFile.size / 1024).toFixed(2)} KB
              </div>
            </div>
          )}
        </div>
        {/* 분석 시작 버튼 */}
        {selectedFile && (
          <button
            onClick={handleStartAnalysis}
            className="prescription-analyze-button"
          >
            <Search className="prescription-button-icon" /> 분석 시작
          </button>
        )}

        {/* 오류 메시지 */}
        {error && (
          <div className="prescription-error-container">
            <AlertCircle className="prescription-error-icon" />
            <strong>오류:</strong> {error}
          </div>
        )}

        {/* 로딩 모달 */}
        {uploading && (
          <div className="prescription-loading-overlay">
            <div className="prescription-loading-modal">
              <Loader className="prescription-loading-spinner" />
              <div className="prescription-loading-text">처방전 분석 중...</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrescriptionAnalysisUpload;
